declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

const GA_SCRIPT_ID = 'google-analytics-script';
const initializedMeasurementIds = new Set<string>();

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function isLocalhost(): boolean {
  if (!isBrowser()) {
    return false;
  }

  return (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === '[::1]'
  );
}

function isDevAnalyticsDebug(): boolean {
  return process.env.NODE_ENV !== 'production' && isLocalhost();
}

function logDebug(message: string, payload?: unknown): void {
  if (!isDevAnalyticsDebug()) {
    return;
  }

  if (payload !== undefined) {
    console.info(`[GA DEBUG] ${message}`, payload);
    return;
  }

  console.info(`[GA DEBUG] ${message}`);
}

export function initGoogleAnalytics(measurementId?: string): void {
  if (!isBrowser() || !measurementId) {
    logDebug('init skipped: missing browser context or measurement ID');
    return;
  }

  const normalizedMeasurementId = measurementId.trim();
  if (!normalizedMeasurementId || initializedMeasurementIds.has(normalizedMeasurementId)) {
    logDebug('init skipped: invalid or already initialized measurement ID', {
      measurementId: normalizedMeasurementId,
    });
    return;
  }

  if (!document.getElementById(GA_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${normalizedMeasurementId}`;
    script.addEventListener('load', () => {
      logDebug('gtag.js loaded successfully');
      if (window.gtag) {
        window.gtag('config', normalizedMeasurementId, {
          send_page_view: true,
          debug_mode: isLocalhost(),
        });
        logDebug('GA config replayed after gtag.js load');
      }
    });
    script.addEventListener('error', () => {
      logDebug('gtag.js failed to load');
    });
    document.head.appendChild(script);
    logDebug('gtag.js script appended', { src: script.src });
  } else {
    const existingScript = document.getElementById(GA_SCRIPT_ID) as HTMLScriptElement | null;
    logDebug('gtag.js script already present', {
      src: existingScript?.src,
    });
  }

  const dataLayer = window.dataLayer || [];
  window.dataLayer = dataLayer;
  if (!window.gtag) {
    // Match the official GA snippet shape: push `arguments` object to dataLayer.
    window.gtag = function gtagShim() {
      dataLayer.push(arguments);
    } as (...args: unknown[]) => void;
    logDebug('window.gtag shim initialized');
  }

  const debugMode = isLocalhost();
  window.gtag('js', new Date());
  window.gtag('config', normalizedMeasurementId, {
    send_page_view: true,
    debug_mode: debugMode,
  });
  initializedMeasurementIds.add(normalizedMeasurementId);
  logDebug('GA initialized', {
    measurementId: normalizedMeasurementId,
    debugMode,
    hasGtag: typeof window.gtag === 'function',
    dataLayerLength: window.dataLayer.length,
    scriptPresent: Boolean(document.getElementById(GA_SCRIPT_ID)),
  });
}

export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>
): void {
  if (!isBrowser() || !window.gtag) {
    logDebug('event skipped: gtag unavailable', { eventName, params });
    return;
  }

  const eventPayload = {
    ...params,
    debug_mode: isLocalhost(),
  };
  window.gtag('event', eventName, eventPayload);
  logDebug('event sent', { eventName, params: eventPayload });
}
